// web/src/features/admin/GroupsAdminDev.jsx
import React from 'react'
import AdminBar from '../../components/AdminBar.jsx'
import ConfirmDialog from '../../components/ConfirmDialog.jsx'

// Tiny pill
function Pill({ children }) { return <span className="pill">{children}</span> }

// Firestore
import { app as firebaseApp } from '@/firebase'
import {
  getFirestore,
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp
} from 'firebase/firestore'

export default function GroupsAdminDev() {
  // Data
  const [groups, setGroups] = React.useState([])
  const [users, setUsers] = React.useState([])
  const [communities, setCommunities] = React.useState([])

  // UI state
  const [q, setQ] = React.useState('')
  const [isAdding, setIsAdding] = React.useState(false)
  const [loading, setLoading] = React.useState(true)
  const [err, setErr] = React.useState('')

  // Draft (new group)
  const [gName, setGName] = React.useState('')
  const [gDesc, setGDesc] = React.useState('')
  const [gActive, setGActive] = React.useState(true)
  const [gMemberEmails, setGMemberEmails] = React.useState([])       // emails
  const [gCommunityValues, setGCommunityValues] = React.useState([]) // names (we save names)

  // Delete confirm
  const [confirmDel, setConfirmDel] = React.useState({ open: false, id: null, name: '' })

  // Notice dialog
  const [notice, setNotice] = React.useState({
    open: false,
    title: 'Please review',
    message: null,
    autoCloseMs: null
  })

  const db = React.useMemo(() => getFirestore(firebaseApp), [])

  /* -------------------- Loaders -------------------- */
  async function loadAll() {
    setLoading(true)
    setErr('')
    try {
      const [gSnap, uSnap, cSnap] = await Promise.all([
        getDocs(collection(db, 'groups')),
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'communities')),
      ])

      const gRows = gSnap.docs.map(d => ({ id: d.id, ...d.data() }))
      const uRows = uSnap.docs.map(d => ({ id: d.id, ...d.data() }))
      const cRows = cSnap.docs.map(d => ({ id: d.id, ...d.data() }))

      setGroups(gRows)
      setUsers(uRows)
      setCommunities(cRows)

      // Debug sample
      console.log('[Groups] loaded', {
        groups: gRows.length, users: uRows.length, communities: cRows.length,
        sampleGroup: gRows[0] || null
      })
    } catch (e) {
      console.error('[Groups] load failed:', e)
      setErr(e?.message || 'Failed to load groups/users/communities.')
    } finally {
      setLoading(false)
    }
  }

  React.useEffect(() => { loadAll() }, [])

  /* -------------- Community lookups (code -> name) -------------- */
  const byCode = React.useMemo(() => {
    const m = new Map()
    for (const c of communities) m.set(String(c.code || ''), c.communityName)
    return m
  }, [communities])
  const nameSet = React.useMemo(
    () => new Set((communities || []).map(c => c.communityName)),
    [communities]
  )

  function toName(value) {
    if (nameSet.has(value)) return value
    if (byCode.has(String(value))) return byCode.get(String(value))
    return value
  }

  /* -------------------- Helpers -------------------- */
  function getMulti(e) {
    return Array.from(e.target.selectedOptions).map(o => o.value)
  }

  function resetDraft() {
    setGName(''); setGDesc(''); setGActive(true)
    setGMemberEmails([]); setGCommunityValues([])
  }

  function showNotice({ title = 'Please review', message, autoCloseMs = 2200 }) {
    setNotice({ open: true, title, message, autoCloseMs })
  }

  /* -------------------- Create -------------------- */
  async function addGroup() {
    // validate
    if (!gName.trim()) {
      showNotice({ title: 'Missing information', message: 'Group name is required.' })
      return
    }
    if (gMemberEmails.length === 0) {
      showNotice({ title: 'Missing information', message: 'Select at least one member.' })
      return
    }
    if (gCommunityValues.length === 0) {
      showNotice({ title: 'Missing information', message: 'Select at least one community.' })
      return
    }

    try {
      const payload = {
        name: gName.trim(),
        description: gDesc.trim(),
        isActive: !!gActive,
        memberEmails: [...gMemberEmails],
        communities: gCommunityValues.map(toName), // save names for readability
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }
      const ref = await addDoc(collection(db, 'groups'), payload)

      // Optimistic insert
      setGroups(prev => [{ id: ref.id, ...payload }, ...prev])

      resetDraft()
      setIsAdding(false)
      showNotice({ title: 'Success', message: 'Group created.', autoCloseMs: 1400 })
    } catch (e) {
      console.error('addGroup failed', e)
      showNotice({ title: 'Error', message: e?.message || 'Failed to create group.' })
    }
  }

  /* -------------------- Toggle Active -------------------- */
  async function toggleActive(g) {
    try {
      const next = !g.isActive
      await updateDoc(doc(db, 'groups', g.id), { isActive: next, updatedAt: serverTimestamp() })
      setGroups(prev => prev.map(x => (x.id === g.id ? { ...x, isActive: next } : x)))
    } catch (e) {
      console.error('toggleActive failed', e)
      showNotice({ title: 'Error', message: e?.message || 'Failed to update group.' })
    }
  }

  /* -------------------- Delete -------------------- */
  function requestRemoveGroup(g) {
    setConfirmDel({ open: true, id: g.id, name: g.name || '' })
  }

  async function finalizeRemoveGroup() {
    try {
      if (!confirmDel.id) { setConfirmDel({ open:false, id:null, name:'' }); return }
      await deleteDoc(doc(db, 'groups', confirmDel.id))
      setGroups(prev => prev.filter(x => x.id !== confirmDel.id))
      setConfirmDel({ open:false, id:null, name:'' })
      showNotice({ title: 'Done', message: 'Group deleted.', autoCloseMs: 1200 })
    } catch (e) {
      console.error('delete group failed', e)
      showNotice({ title: 'Error', message: e?.message || 'Failed to delete group.' })
    }
  }

  function cancelRemoveGroup() {
    setConfirmDel({ open:false, id:null, name:'' })
  }

  /* -------------------- Filter -------------------- */
  const filtered = (groups || []).filter(g => {
    const hay = [
      g.name, g.description,
      ...(g.memberEmails || []),
      ...(g.communities || [])
    ].join(' ').toLowerCase()
    return hay.includes(q.toLowerCase())
  })

  /* -------------------- Render -------------------- */
  return (
    <div>
      <h2>Groups</h2>
      <AdminBar title="" subtitle="" />

      {/* Search + New Group toggle */}
      <div className="card" style={{display:'flex', gap:8, alignItems:'end'}}>
        <div style={{flex:1}}>
          <input
            placeholder="Search name/description…"
            value={q}
            onChange={e=>setQ(e.target.value)}
          />
        </div>
        {!isAdding ? (
          <button onClick={()=>{ resetDraft(); setIsAdding(true) }}>+ New Group</button>
        ) : (
          <button className="secondary" onClick={()=>{ resetDraft(); setIsAdding(false) }}>Cancel</button>
        )}
      </div>

      {/* New Group editor */}
      {isAdding && (
        <div className="card">
          <h3>New Group</h3>

          {err && <div style={{color:'#b00', marginBottom:8}}>{err}</div>}

          <div className="grid2" style={{alignItems:'start', gap:16}}>
            <div>
              <label>Name</label>
              <input value={gName} onChange={e=>setGName(e.target.value)} placeholder="Group name" />

              <label style={{marginTop:8}}>Members (multi-select)</label>
              <select
                multiple
                size={Math.min(10, (users||[]).length || 5)}
                value={gMemberEmails}
                onChange={e=>setGMemberEmails(getMulti(e))}
                style={{width:'100%', minWidth:280}}
              >
                {(users||[]).map((u,i)=>(
                  <option key={u.id || u.email || i} value={u.email}>
                    {u.email} {u.title ? `— ${u.title}` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label>Description</label>
              <input value={gDesc} onChange={e=>setGDesc(e.target.value)} placeholder="Optional" />

              <label style={{marginTop:8}}>Communities (multi-select)</label>
              <select
                multiple
                size={Math.min(12, (communities||[]).length || 6)}
                value={gCommunityValues}
                onChange={e=>setGCommunityValues(getMulti(e))}
                style={{width:'100%', minWidth:320}}
              >
                {(communities||[]).map((c,i)=>(
                  <option key={c.id || c.code || c.communityName || i} value={c.communityName}>
                    {c.communityName}
                  </option>
                ))}
              </select>

              <div style={{marginTop:8}}>
                <label style={{display:'inline-flex', alignItems:'center', gap:6}}>
                  <input type="checkbox" checked={gActive} onChange={e=>setGActive(e.target.checked)} />
                  Active
                </label>
              </div>
            </div>
          </div>

          <div style={{marginTop:12, display:'flex', gap:8}}>
            <button onClick={addGroup} disabled={loading}>Save</button>
            <button className="secondary" onClick={()=>{ resetDraft(); setIsAdding(false) }}>Cancel</button>
          </div>

          <p className="muted" style={{marginTop:8}}>
            We save <strong>community names</strong> for readability. If a tracking number requires a code,
            we’ll map name → code at generation time.
          </p>
        </div>
      )}

      {/* List */}
      <div className="card">
        {loading ? (
          <div className="muted">Loading…</div>
        ) : err ? (
          <div style={{color:'#b00'}}>{err}</div>
        ) : (
          <table className="table groups-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Members</th>
                <th>Communities</th>
                <th>Active</th>
                <th className="actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((g, idx) => (
                <tr key={g.id || g.name || idx}>
                  <td style={{minWidth:220}}>
                    <strong>{g.name}</strong>
                    {g.description ? <div style={{fontSize:12,opacity:.75}}>{g.description}</div> : null}
                  </td>
                  <td style={{maxWidth:360}}>
                    <div className="pillWrap">
                      {(g.memberEmails || []).map((em,i)=>(
                        <Pill key={`${g.id}-mem-${em}-${i}`}>{em}</Pill>
                      ))}
                    </div>
                  </td>
                  <td>
                    <div className="pillWrap">
                      {(g.communities || []).map((val,i)=>(
                        <Pill key={`${g.id}-com-${val}-${i}`}>{toName(val)}</Pill>
                      ))}
                    </div>
                  </td>
                  <td>{g.isActive ? 'Yes' : 'No'}</td>
                  <td className="actions">
                    <div className="btn-row">
                      <button className="btn" onClick={()=>toggleActive(g)}>
                        {g.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button className="btn danger" onClick={()=>requestRemoveGroup(g)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan="5" style={{opacity:.7}}>No groups match.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Confirm delete */}
      <ConfirmDialog
        open={confirmDel.open}
        title="Delete group"
        message={
          <>
            You’re about to delete the group{' '}
            <strong>{confirmDel.name || 'Untitled Group'}</strong>.
            <br />
            This removes the group configuration but does not delete any users.
          </>
        }
        confirmLabel="Delete Group"
        cancelLabel="Cancel"
        tone="danger"
        onConfirm={finalizeRemoveGroup}
        onCancel={cancelRemoveGroup}
      />

      {/* Notice / Success (auto-dismiss supported by the component) */}
      <ConfirmDialog
        open={notice.open}
        title={notice.title}
        message={notice.message}
        confirmLabel="OK"
        cancelLabel="Close"
        tone="default"
        autoCloseMs={notice.autoCloseMs}
        onConfirm={() => setNotice(n => ({ ...n, open: false }))}
        onCancel={() => setNotice(n => ({ ...n, open: false }))}
      />
    </div>
  )
}